// src/screens/JoinWaitlist.js
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "./JoinWaitlist.css";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../img/logo.png";
import axios from "axios";

const API_BASE = "https://revexia.ai/api/";

// ✅ Email validation helper
const validateEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

function JoinWaitlist() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState(""); // ✅ email-specific error

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    website: "",
    sellingChannel: "",
    biggestProblem: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  const handleLogout = () => {
    logout();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // ✅ Clear email error while user is typing
    if (name === "email") {
      setEmailError("");
    }
  };

  // ✅ Validate email on blur
  const handleEmailBlur = () => {
    if (formData.email.trim() && !validateEmail(formData.email)) {
      setEmailError("Please enter a valid email address.");
    } else {
      setEmailError("");
    }
  };

  const handleNext = () => {
    // ✅ Block Step 2 progression if email is invalid
    if (currentStep === 2 && !validateEmail(formData.email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    if (currentStep < 5) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError("");

    try {
      await axios.post(
        `${API_BASE}/waitlist`,
        {
          name: formData.name,
          email: formData.email,
          current_selling_channel: formData.sellingChannel,
          dm_challenge: formData.biggestProblem,
        },
        {
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
          },
        }
      );

      setIsSubmitted(true);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        err.message ||
        "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.name.trim() !== "";
      case 2: return formData.email.trim() !== "" && validateEmail(formData.email); // ✅ also checks format
      case 3: return formData.website.trim() !== "";
      case 4: return formData.sellingChannel !== "";
      case 5: return formData.biggestProblem !== "";
      default: return false;
    }
  };

  const sellingChannelOptions = [
    "Shopify",
    "Woocommerce",
    "Creator store (Stan, SuperProfile, etc.)",
    "Instagram / DMs only",
    "Not selling yet",
  ];

  const biggestProblemOptions = [
    "I can't tell who is ready to buy",
    "Too many DMs, not enough time",
    "DMs don't convert into sales",
    "I reply manually to everyone",
  ];

  // ─── Navbar ───────────────────────────────────────────────────────────────
  const Navbar = () => (
    <nav className="navbar-custom">
      <div className="navbar-container">
        <div className="navbar-brand-custom">
          <a href="/">
            <img src={Logo} alt="Revexia Logo" className="navbar-logo-img" />
          </a>
        </div>
        <div className="navbar-center-pill">
          <ul className="nav-menu-pills">
            <li className="nav-item-pill"><a href="/" className="nav-link-pill">Home</a></li>
            <li className="nav-item-pill"><a href="/#features" className="nav-link-pill">Features</a></li>
            <li className="nav-item-pill"><a href="/#resources" className="nav-link-pill">Resources</a></li>
            <li className="nav-item-pill"><a href="/#contact" className="nav-link-pill">Contact</a></li>
          </ul>
        </div>
        <div className="navbar-auth-section">
          {user ? (
            <>
              <Link to="/dashboard" className="btn-nav-outline">Dashboard</Link>
              <button onClick={handleLogout} className="btn-nav-gradient">Logout</button>
            </>
          ) : (
            <>
                             <Link to="/join-waitlist" className="btn-nav-gradient">
                                                Get Started
                                              </Link>
                                                <Link to="/auth" className="btn-nav-gradientlogin">
                                                Login
                                              </Link>
                           </>
          )}
        </div>
        <button
          className="navbar-mobile-toggle"
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span className="navbar-toggler-icon-custom"></span>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="navbar-mobile-menu">
          <ul className="mobile-nav-list">
            <li><a href="/" onClick={() => setMobileMenuOpen(false)}>Home</a></li>
            <li><a href="/#features" onClick={() => setMobileMenuOpen(false)}>Features</a></li>
            <li><a href="/#resources" onClick={() => setMobileMenuOpen(false)}>Resources</a></li>
            {/* <li><a href="/contact" onClick={() => setMobileMenuOpen(false)}>Contact</a></li> */}
          </ul>
          <div className="mobile-auth-buttons">
            {user ? (
              <>
                <Link to="/dashboard" className="btn-nav-outline" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="btn-nav-gradient">Logout</button>
              </>
            ) : (
              <>
                <Link to="/auth" className="btn-nav-outline" onClick={() => setMobileMenuOpen(false)}>Login</Link>
                <Link to="/join-waitlist" className="btn-nav-gradient" onClick={() => setMobileMenuOpen(false)}>Get started</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );

  // ─── Footer ───────────────────────────────────────────────────────────────
  const Footer = () => (
    <footer className="footer-v2" id="contact">
      <div className="container-footer-v2">
        <div className="footer-main-content">
          <div className="footer-brand-section-v2">
            <div className="footer-logo-wrapper">
              <img src={Logo} alt="Revexia" className="footer-logo-image" />
            </div>
            <p className="footer-brand-description-v2">
              A platform to turn social media DM engagement into automated revenue with AI-driven personalization.
            </p>
          </div>
          <div className="footer-column-v2">
            <h4 className="footer-column-heading-v2">Informations</h4>
            <ul className="footer-column-links-v2">
              <li><a href="/">Home</a></li>
              
            </ul>
          </div>
          <div className="footer-column-v2">
            <h4 className="footer-column-heading-v2">Social</h4>
            <ul className="footer-column-links-v2">
              <li><a href="https://facebook.com" target="_blank" rel="noopener noreferrer">Facebook</a></li>
              <li><a href="https://instagram.com" target="_blank" rel="noopener noreferrer">Instagram</a></li>
              <li><a href="https://twitter.com" target="_blank" rel="noopener noreferrer">Twitter</a></li>
              <li><a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">LinkedIn</a></li>
              <li><a href="https://tiktok.com" target="_blank" rel="noopener noreferrer">TikTok</a></li>
            </ul>
          </div>
          <div className="footer-column-v2">
            <h4 className="footer-column-heading-v2">Help</h4>
            <ul className="footer-column-links-v2">
              {/* <Link to="/contact">Contact</Link> */}
              
              <li><Link to="/terms">Terms & conditions</Link></li>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>
        <hr className="footer-divider-line-v2" />
        <div className="footer-bottom-v2">
          <p className="footer-copyright-v2">
            © 2026 Revexia. All rights reserved. Developed by{" "}
            <a href="https://softwarebari.com" target="_blank" rel="noopener noreferrer" className="developer-link">
              Softwarebari
            </a>.
          </p>
          <div className="footer-legal-links-v2">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <span className="separator-v2">|</span>
            <Link to="/terms">Terms &amp; Conditions</Link>
            <span className="separator-v2">|</span>
            <a href="/cookies">Cookies Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );

  // ─── Thank You Screen ─────────────────────────────────────────────────────
  if (isSubmitted) {
    return (
      <>
        <Helmet><title>Thank You - Revexia Waitlist</title></Helmet>
        <div className="homepage">
          <Navbar />
          <div className="waitlist-thank-you-wrapper">
            <div className="waitlist-thank-you-container">
              <div className="thank-you-icon">
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                  <circle cx="40" cy="40" r="38" stroke="#5B7FFF" strokeWidth="4" />
                  <path d="M25 40L35 50L55 30" stroke="#5B7FFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h1 className="thank-you-title">Thank You!</h1>
              <p className="thank-you-message">
                You're on the waitlist! We'll send early access details to <strong>{formData.email}</strong>
              </p>
              <p className="thank-you-submessage">
                Get ready to transform your DM conversations into revenue with AI.
              </p>
              <Link to="/" className="btn-back-home">Back to Home</Link>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  // ─── Main Form ────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>Join Waitlist - Revexia</title>
        <meta
          name="description"
          content="Join the Revexia early access waitlist. Be the first to transform your DM conversations into revenue with AI."
        />
      </Helmet>

      <div className="homepage">
        <Navbar />

        <div className="waitlist-content-wrapper">
          <div className="waitlist-header-section">
            <div className="waitlist-container">
              <h1 className="waitlist-main-title">Join the early access waitlist</h1>
              <p className="waitlist-subtitle">Takes less than 30 seconds</p>
              <div className="progress-bar-container">
                <div className="progress-bar-bg">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${(currentStep / 5) * 100}%` }}
                  ></div>
                </div>
                <p className="progress-text">Step {currentStep} of 5</p>
              </div>
            </div>
          </div>

          <div className="waitlist-form-section">
            <div className="waitlist-container">
              <div className="form-card">

                {/* Step 1: Name */}
                {currentStep === 1 && (
                  <div className="form-step">
                    <div className="step-number">1</div>
                    <h2 className="step-question">What's your name?</h2>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g. Alex Morgan"
                      className="form-input"
                      autoFocus
                    />
                  </div>
                )}

                {/* Step 2: Email */}
                {currentStep === 2 && (
                  <div className="form-step">
                    <div className="step-number">2</div>
                    <h2 className="step-question">What's your email?</h2>
                    <p className="step-description">We'll send early access here.</p>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleEmailBlur} // ✅ validate on blur
                      placeholder="you@company.com"
                      className={`form-input ${emailError ? "input-error" : ""}`} // ✅ error styling hook
                      autoFocus
                    />
                    {/* ✅ Inline email error message */}
                    {emailError && (
                      <p className="field-error-text">{emailError}</p>
                    )}
                  </div>
                )}

                {/* Step 3: Website */}
                {currentStep === 3 && (
                  <div className="form-step">
                    <div className="step-number">3</div>
                    <h2 className="step-question">Where do you currently sell online?</h2>
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://yourstore.com or Stan"
                      className="form-input"
                      autoFocus
                    />
                  </div>
                )}

                {/* Step 4: Selling Channel */}
                {currentStep === 4 && (
                  <div className="form-step">
                    <div className="step-number">4</div>
                    <h2 className="step-question">What's your primary selling channel today?</h2>
                    <div className="options-grid">
                      {sellingChannelOptions.map((option, index) => (
                        <button
                          key={index}
                          className={`option-button ${formData.sellingChannel === option ? "selected" : ""}`}
                          onClick={() => setFormData((prev) => ({ ...prev, sellingChannel: option }))}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 5: Biggest Problem */}
                {currentStep === 5 && (
                  <div className="form-step">
                    <div className="step-number">5</div>
                    <h2 className="step-question">What's your biggest challenge with DMs right now?</h2>
                    <div className="options-grid">
                      {biggestProblemOptions.map((option, index) => (
                        <button
                          key={index}
                          className={`option-button ${formData.biggestProblem === option ? "selected" : ""}`}
                          onClick={() => setFormData((prev) => ({ ...prev, biggestProblem: option }))}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Error Banner */}
                {error && (
                  <div className="auth-error-banner" style={{ marginTop: "16px" }}>
                    <span>⚠ {error}</span>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="form-navigation">
                  {currentStep > 1 && (
                    <button onClick={handleBack} className="btn-back" disabled={isLoading}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Back
                    </button>
                  )}

                  {currentStep < 5 ? (
                    <button onClick={handleNext} className="btn-next" disabled={!canProceed()}>
                      Next
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      className="btn-submit"
                      disabled={!canProceed() || isLoading}
                    >
                      {isLoading ? "Submitting..." : "Join Waitlist"}
                    </button>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}

export default JoinWaitlist;
